/**
 * Data Access Layer for login sessions (rotating refresh tokens).
 *
 * One row per login. The API stores only the SHA-256 of the current refresh token
 * and rotates it on every refresh; the token logic itself (hashing, expiry windows,
 * reuse detection) lives in the API's session service. This module is plain storage.
 *
 * user_sessions is service-role only (RLS on, no policies, no anon/authenticated
 * grants), exactly like auth_events: always go through the admin client.
 */

import { getClient, getAdminClient } from './client.js';
import { TABLES } from './tables.js';

export interface UserSession {
  id: string;
  user_id: string;
  account_id: string;
  token_hash: string;
  previous_token_hash: string | null;
  rotated_at: string | null;
  created_at: string;
  last_used_at: string;
  expires_at: string;
  revoked_at: string | null;
  revoked_reason: string | null;
  ip_address: string | null;
  user_agent: string | null;
}

export interface UserSessionCreate {
  user_id: string;
  account_id: string;
  token_hash: string;
  expires_at: string;
  ip_address?: string | null;
  user_agent?: string | null;
}

function sessionsClient() {
  return getAdminClient() ?? getClient();
}

export async function createUserSession(input: UserSessionCreate): Promise<UserSession> {
  const { data, error } = await sessionsClient()
    .from(TABLES.user_sessions)
    .insert({
      user_id: input.user_id,
      account_id: input.account_id,
      token_hash: input.token_hash,
      expires_at: input.expires_at,
      ip_address: input.ip_address ?? null,
      user_agent: input.user_agent ?? null,
    })
    .select('*')
    .single();

  if (error) throw error;
  return data as UserSession;
}

export async function getUserSessionByTokenHash(tokenHash: string): Promise<UserSession | null> {
  const { data, error } = await sessionsClient()
    .from(TABLES.user_sessions)
    .select('*')
    .eq('token_hash', tokenHash)
    .maybeSingle();

  if (error) throw error;
  return (data as UserSession) ?? null;
}

export async function getUserSessionByPreviousTokenHash(tokenHash: string): Promise<UserSession | null> {
  const { data, error } = await sessionsClient()
    .from(TABLES.user_sessions)
    .select('*')
    .eq('previous_token_hash', tokenHash)
    .maybeSingle();

  if (error) throw error;
  return (data as UserSession) ?? null;
}

/**
 * Swap the session's refresh token, but only if it still holds `currentHash`.
 * The conditional update is the compare-and-swap that stops two concurrent
 * refreshes from both rotating: the loser gets null back.
 */
export async function rotateUserSessionToken(
  id: string,
  currentHash: string,
  nextHash: string,
): Promise<UserSession | null> {
  const now = new Date().toISOString();
  const { data, error } = await sessionsClient()
    .from(TABLES.user_sessions)
    .update({
      token_hash: nextHash,
      previous_token_hash: currentHash,
      rotated_at: now,
      last_used_at: now,
    })
    .eq('id', id)
    .eq('token_hash', currentHash)
    .is('revoked_at', null)
    .select('*')
    .maybeSingle();

  if (error) throw error;
  return (data as UserSession) ?? null;
}

export async function touchUserSession(id: string): Promise<void> {
  const { error } = await sessionsClient()
    .from(TABLES.user_sessions)
    .update({ last_used_at: new Date().toISOString() })
    .eq('id', id);

  if (error) throw error;
}

export async function revokeUserSession(id: string, reason: string): Promise<void> {
  const { error } = await sessionsClient()
    .from(TABLES.user_sessions)
    .update({ revoked_at: new Date().toISOString(), revoked_reason: reason })
    .eq('id', id)
    .is('revoked_at', null);

  if (error) throw error;
}

export async function revokeAllUserSessions(userId: string, reason: string): Promise<void> {
  const { error } = await sessionsClient()
    .from(TABLES.user_sessions)
    .update({ revoked_at: new Date().toISOString(), revoked_reason: reason })
    .eq('user_id', userId)
    .is('revoked_at', null);

  if (error) throw error;
}
